import React from "react";
import { FieldPickerField } from "../../components/field-picker/components/fields/fields.types";
import { DatasheetElement, DatasheetElementId, DatasheetElementType } from "@modules/datasheets/types/datasheet-element";
import { getUniqueFieldCombination as getUniqueFieldCombinationBase } from "@common/utils/fields";
import { useDeleteKeyPress } from "@common/hooks/delete-press";
import { DATASHEET_DEFAULT_FIELDS_DIMENSIONS, textElementDimensions } from "../../editor.constants";
import { LocalDatasheetTabDto } from "../../types/tab-dto";
import { useDatasheetSavingController } from "./datasheet-save";
import { useDatasheetElementsSelectionController } from "./selection";
import { useDatasheetFormatManagementController } from "./format-management";
import { DatasheetGridSettingsDto } from "@common/dto/datasheet/grid-settings-dto";
import { useTabsController } from "./tabs";
import { DatasheetFieldComponentGetDto } from "@common/dto/datasheet/field-component/get-dto";
import { DatasheetElementSettingsFieldComponentValue, DatasheetElementSettingsTextComponentValue } from "../../components/datasheet-element-settings/controllers/main";
import { DatasheetFieldComponentTextAlign } from "@common/dto/datasheet/field-component/text-align";
import { DatasheetFieldComponentHeadingPosition } from "@common/dto/datasheet/field-component/heading-position";
import { useDatasheetElementSettingsController } from "./datasheet-element-settings";
import { useDatasheetElementsAlignmentController } from "./alignment";
import { DatasheetTextComponentGetDto } from "@common/dto/datasheet/text-component/get-dto";
import { generateId } from "@common/utils/common";
import { DatasheetComponentTextStyle } from "@common/dto/datasheet/component-settings/text-style";
import { useDatasheetElementsCopyPasteController } from "./copy-paste";
import { DatasheetFieldComponentOriginFieldGetDto } from "@common/dto/datasheet/field-component/origin-field-dto";
import { useGridSettingsController } from "./grid-settings";
import { useDatasheetUndoRedoController } from "./undo-redo";
import { DatasheetStorage } from "@modules/datasheets/utils/datasheet-storage";
import { DatasheetLocalGetDto } from "../../types/datasheet";
import { FieldType } from "@common/dto/field/type";
import { buildDatasheetElementsByTab } from "@modules/datasheets/utils/datasheet-elements";
import { saveCopiedTabToStorage, loadCopiedTabFromStorage, clearCopiedTabFromStorage, hasCopiedTabInStorage } from "@modules/datasheets/utils/copied-tab-storage";
import { resolveFieldsToAddOnDrop } from "./field-drop-selection";
import { getDroppedFieldYCoordinate } from "./field-drop-placement";
import { FieldPrimaryListDto } from "@common/dto/field/list-primary-dto";
import { isFieldTypeTabNavigable, NON_TAB_FIELD_TYPES } from "@modules/datasheets/utils/tab-navigation";

// Clipboard shared across all editor instances (separate GWT DS editor tabs share the same JS module).
// The DOM event keeps already-mounted instances in sync when another instance copies.
const CLIPBOARD_CHANGE_EVENT = 'datasheet-clipboard-change';
let _datasheetClipboard: Array<DatasheetElement> = [];

type TabLike = {
  id: string | number;
  fields?: Array<any>;
  texts?: Array<any>;
};

export type DatasheetControllerProps = {
  selectedFieldsInFieldPicker: Array<FieldPickerField>;
  onSelectedFieldsInFieldPickerChange?: (fields: Array<FieldPickerField>) => void;
  datasheet: DatasheetLocalGetDto;
  onDatasheetChange: (datasheet: DatasheetLocalGetDto) => void;
  formatId?: number;
  formatReloadKey?: number;
  onReloadFormat?: (formatId: number, formatName?: string) => void;
  tableFieldId?: number | null;
  loadTabFormat?: (tabId: number) => Promise<void> | void;
};

export const useDatasheetController = (props: DatasheetControllerProps) => {
  const { selectedFieldsInFieldPicker, onSelectedFieldsInFieldPickerChange, datasheet, onDatasheetChange ,formatId, formatReloadKey, onReloadFormat, tableFieldId, loadTabFormat } = props;
  const datasheetElementsStorage = React.useMemo<DatasheetStorage>(() => new DatasheetStorage(), []);
  // TODO review usage of currentTabId
  const [currentTabId, setCurrentTabId] = React.useState<LocalDatasheetTabDto['id']>(1);
  const currentTabIdRef = React.useRef<LocalDatasheetTabDto['id']>(1);
  const pendingTabSelectionRef = React.useRef<LocalDatasheetTabDto['id'] | null>(null);
  const justPastedTabRef = React.useRef<LocalDatasheetTabDto['id'] | null>(null);
  
  // Sync ref with state
  React.useEffect(() => {
    currentTabIdRef.current = currentTabId;
  }, [currentTabId]);
  const [currentDatasheetElementsHistoryRecordId, setCurrentDatasheetElementsHistoryRecordId] = React.useState<string>();
  const [copiedDatasheetElements, setCopiedDatasheetElements] = React.useState<Array<DatasheetElement>>(
    () => _datasheetClipboard
  );
  const [datasheetElements, setDatasheetElements] = React.useState<Record<DatasheetElementId, DatasheetElement>>({});
  const [selectedDatasheetElementsId, setSelectedDatasheetElementsId] = React.useState<Array<DatasheetElementId>>([]);
  // Use ref to persist copied elements across tab deletions and switches
  const copiedDatasheetElementsFromTabRef = React.useRef<Record<DatasheetElementId, DatasheetElement>>({});
  const [copiedDatasheetElementsFromTab, setCopiedDatasheetElementsFromTab] = React.useState<Record<DatasheetElementId, DatasheetElement>>({});
  // Store copied tab elements separately from the tab object
  const copiedTabElementsRef = React.useRef<Record<DatasheetElementId, DatasheetElement>>({});
  // Track if a tab has been copied (even if empty) to enable paste
  const [hasCopiedTab, setHasCopiedTab] = React.useState<boolean>(false);
  const hasCopiedTabRef = React.useRef<boolean>(false);
  
  // Sync ref with state to ensure persistence
  React.useEffect(() => {
    copiedDatasheetElementsFromTabRef.current = copiedDatasheetElementsFromTab;
  }, [copiedDatasheetElementsFromTab]);
  
  // Sync hasCopiedTab ref with state
  React.useEffect(() => {
    hasCopiedTabRef.current = hasCopiedTab;
  }, [hasCopiedTab]);
  const [defaultFieldComponentSettings, setDefaultFieldComponentSettings] = React.useState<DatasheetElementSettingsFieldComponentValue>({
    content: {
      fontFamily: 'Arial',
      fontSize: 10,
      color: '#000000',
      showBorder: true,
      showFieldType: true,
      styles: [],
      align: DatasheetFieldComponentTextAlign.left,
      tabOrder: 1
    },
    heading: {
      displayHeading: true,
      textValue: '',
      fontFamily: 'Tahoma',
      fontSize: 8,
      color: '#000000',
      position: DatasheetFieldComponentHeadingPosition.left,
      align: DatasheetFieldComponentTextAlign.left,
      textStyles: []
    },
    helpText: {
      textValue: ''
    },
    table: undefined
  });

  const [defaultTextComponentSettings, setDefaultTextComponentSettings] = React.useState<DatasheetElementSettingsTextComponentValue>({
    fontFamily: 'Arial',
    fontSize: 14,
    color: '#000000',
    textStyle: [],
    text: '',
    align: DatasheetFieldComponentTextAlign.left,
  });

  const normalizeDatasheetElementsTabOrder = React.useCallback((els: Record<DatasheetElementId, DatasheetElement>) => {
    const elements = Object.values(els);
    type Field = DatasheetElement<DatasheetFieldComponentGetDto>;
    const sortedElements = elements.sort((a, b) => (a as Field).original.tabOrder - (b as Field).original.tabOrder);
    let tabOrder = 0;
    let systemTabOrder = 0;
    let nonTabOrder = 0;

    const nextElements = sortedElements.reduce<Record<DatasheetElementId, DatasheetElement>>((acc, element) => {
      if (element.type === DatasheetElementType.field) {
        const fieldEl = element as Field;
        if (fieldEl.original.field?.system) {
          const sysUpdated: Field = {
            ...fieldEl,
            original: { ...fieldEl.original, tabOrder: ++systemTabOrder },
          };
          acc[element.id] = sysUpdated;
          return acc;
        }

        const f = fieldEl.original.field;
        const ft = (f as any)?.fieldtype?.typeid ?? (f as any)?.type ?? (f as any)?.rawFieldType;
        if (NON_TAB_FIELD_TYPES.has(ft)) {
          const nonTabEl: Field = {
            ...fieldEl,
            original: { ...fieldEl.original, tabOrder: ++nonTabOrder },
          };
          acc[element.id] = nonTabEl;
          return acc;
        }

        const updatedElement = {
          ...element,
          original: {
            ...element.original,
            tabOrder: ++tabOrder,
          },
        };

        acc[element.id] = updatedElement;

        return acc;
      }

      acc[element.id] = element;

      return acc;
    }, {});

    return nextElements;
  }, []);

  const addNewDatasheetElementsHistoryRecord = React.useCallback((elements: Record<DatasheetElementId, DatasheetElement>, tabId?: LocalDatasheetTabDto['id']) => {
    const newHistoryRecordId = generateId();

    datasheetElementsStorage.addHistoryRecord({
      tabId: tabId ?? currentTabId!,
      currentHistoryRecordId: currentDatasheetElementsHistoryRecordId!,
      newHistoryRecordId,
      elementsForNewHistoryRecord: elements
    });

    setCurrentDatasheetElementsHistoryRecordId(newHistoryRecordId);
  }, [currentTabId, currentDatasheetElementsHistoryRecordId]);

  const updateDatasheetElementsHistoryRecord = React.useCallback((elements: Record<DatasheetElementId, DatasheetElement>, params?: { tabId?: LocalDatasheetTabDto['id'] }) => {
    datasheetElementsStorage.updateHistoryRecord({
      tabId: params?.tabId ?? currentTabId!,
      historyRecordId: currentDatasheetElementsHistoryRecordId!,
      elements
    });
  }, [currentTabId, currentDatasheetElementsHistoryRecordId]);

  const datasheetUndoRedoController = useDatasheetUndoRedoController({
    currentHistoryRecordId: currentDatasheetElementsHistoryRecordId,
    getDatasheetElementsHistorySequence: () => datasheetElementsStorage.getHistorySequence(currentTabId!),
  });

  const setTabDatasheetElements = React.useCallback((elements: Record<DatasheetElementId, DatasheetElement>, tabId?: LocalDatasheetTabDto['id']) => {
    // Use ref to get the most current tab ID to ensure elements are saved to the correct tab
    const targetTabId = tabId ?? currentTabIdRef.current;
    if (targetTabId) {
      datasheetElementsStorage.setElements(targetTabId, elements);
    }
    setDatasheetElements(elements);
  }, []);

  const setTabDatasheetElementsAndUpdateHistoryRecord = React.useCallback((elements: Record<DatasheetElementId, DatasheetElement>) => {
    setTabDatasheetElements(elements);
    updateDatasheetElementsHistoryRecord(elements);
  }, [setTabDatasheetElements, updateDatasheetElementsHistoryRecord]);

  const setTabDatasheetElementsAndAddHistoryRecord = React.useCallback((elements: Record<DatasheetElementId, DatasheetElement>) => {
    setTabDatasheetElements(elements);
    addNewDatasheetElementsHistoryRecord(elements);
  }, [setTabDatasheetElements, addNewDatasheetElementsHistoryRecord]);

  const getDatasheetElement = React.useCallback((id: DatasheetElementId) => {
    return datasheetElements[id];
  }, [datasheetElements]);

  const getUniqueDatasheetElementId = (field: { fieldid: number; systemField: boolean; type: FieldType; }) => getUniqueFieldCombinationBase({
    id: field.fieldid,
    system: field.systemField,
    type: field.type,
  });


  const doesFieldExistOnCurrentTab = React.useCallback((field: DatasheetFieldComponentOriginFieldGetDto) => {
    const id = getUniqueDatasheetElementId(field);
    const legacyId = `field-${field.fieldid}`;

    return Boolean(datasheetElements[id] || datasheetElements[legacyId]);
  }, [datasheetElements]);

  const resetSelectedDatasheetElements = React.useCallback(() => {
    setSelectedDatasheetElementsId([]);
  }, []);

  const resetSelectedAndCopiedDatasheetElements = React.useCallback(() => {
    setSelectedDatasheetElementsId([]);
    setCopiedDatasheetElements([]);
    _datasheetClipboard = [];
    window.dispatchEvent(new Event(CLIPBOARD_CHANGE_EVENT));
  }, []);

  // Keep this instance's paste-button state in sync when another DS editor instance copies.
  React.useEffect(() => {
    const handleClipboardChange = () => {
      setCopiedDatasheetElements([..._datasheetClipboard]);
    };
    window.addEventListener(CLIPBOARD_CHANGE_EVENT, handleClipboardChange);
    return () => window.removeEventListener(CLIPBOARD_CHANGE_EVENT, handleClipboardChange);
  }, []);

  const gridSettingsController = useGridSettingsController({ 
    editTabOrderActive: datasheet.gridSettings.showEditTabOrder,
    datasheet, 
    datasheetElements, 
    onDatasheetElementsChange: setTabDatasheetElementsAndUpdateHistoryRecord 
  });
  const getTabElements = React.useCallback((tabId: string | number) => {
    return datasheetElementsStorage.getElements(tabId);
  }, []);
  const datasheetSavingController = useDatasheetSavingController({
    defaultFieldComponentSettings,
    defaultTextComponentSettings,
    datasheet,
    formatId: formatId ?? datasheet.id,
    getTabElements,
    onSaveComplete: onReloadFormat,
    tableFieldId,
  });
  const datasheetElementsSelectionController = useDatasheetElementsSelectionController({
    selectedDatasheetElementsId,
    onSelectedDatasheetElementsIdChange: setSelectedDatasheetElementsId,
    onSelectedDatasheetElementsReset: resetSelectedDatasheetElements,
    datasheetElements,
  });
  const datasheetElementsAlignmentController = useDatasheetElementsAlignmentController({
    datasheetElements,
    selectedDatasheetElementsId,
    onDatasheetElementsChange: setTabDatasheetElementsAndAddHistoryRecord,
    getDatasheetElement,
  });
  const datasheetFormatManagementController = useDatasheetFormatManagementController();
  const tabsController = useTabsController({ 
    datasheet, 
    onDatasheetChange, 
    currentTabId, 
    copiedDatasheetElementsFromTab,
    hasCopiedTab
  });
  const datasheetElementSettingsController = useDatasheetElementSettingsController({
    normalizeDatasheetElementsTabOrder,
    datasheetElements,
    selectedDatasheetElementsId,
    onDatasheetElementsChange: setTabDatasheetElementsAndUpdateHistoryRecord,
    getDatasheetElement,
  });
  // Wrapper to ensure current tab's elements are saved before pasting
  const handleDatasheetElementsPasteWithSave = React.useCallback((elements: Record<DatasheetElementId, DatasheetElement>) => {
    // Ensure current tab's elements are saved to storage before pasting
    // This prevents elements from disappearing when switching back to the source tab
    // Use ref to get the most current tab ID
    const currentTab = currentTabIdRef.current;
    if (currentTab && datasheetElements) {
      datasheetElementsStorage.setElements(currentTab, datasheetElements);
    }
    // Now update with pasted elements
    setTabDatasheetElementsAndAddHistoryRecord(elements);
  }, [datasheetElements, setTabDatasheetElementsAndAddHistoryRecord]);

  const datasheetElementsCopyPasteController = useDatasheetElementsCopyPasteController({
    datasheetElementsOnCurrentTab: datasheetElements,
    onDatasheetElementsCopyReset: resetSelectedAndCopiedDatasheetElements,
    doesFieldExistOnCurrentTab,
    getUniqueDatasheetElementId,
    selectedDatasheetElementsId,
    getDatasheetElement,
    onDatasheetElementsChange: handleDatasheetElementsPasteWithSave,
    copiedDatasheetElements,
    onDatasheetElementsCopy: (elements: Array<DatasheetElement>) => {
      _datasheetClipboard = [...elements];
      setCopiedDatasheetElements(elements);
      window.dispatchEvent(new Event(CLIPBOARD_CHANGE_EVENT));
    },
  });

  const previousDatasheetId = React.useRef<number | undefined>(datasheet.id);
  const previousFormatReloadKey = React.useRef<number | undefined>(formatReloadKey);

  React.useEffect(() => {
    if (datasheet.tabs.length) {
      const isNewDatasheet = previousDatasheetId.current !== datasheet.id;
      const hasReloadedFormat = formatReloadKey !== previousFormatReloadKey.current;
      const shouldResetCache = isNewDatasheet || hasReloadedFormat;

      if (shouldResetCache) {
        previousDatasheetId.current = datasheet.id;
        previousFormatReloadKey.current = formatReloadKey;
      }
      
      const elementsByTab = buildDatasheetElementsByTab(datasheet.tabs as TabLike[]);
      
      // Ensure all tabs are registered in storage
      datasheet.tabs.forEach((tab) => {
        datasheetElementsStorage.addTab(tab.id);
      });

      // Try to preserve the current tab selection if it still exists
      // Check pendingTabSelectionRef first (set by handleTabDelete)
      // Check justPastedTabRef (set by handleTabPaste) to prevent overwriting pasted elements
      // Otherwise, fall back to the first tab (only on new datasheet load)
      let selectedTab: LocalDatasheetTabDto | undefined;
      
      if (pendingTabSelectionRef.current) {
        selectedTab = datasheet.tabs.find((tab) => tab.id === pendingTabSelectionRef.current);
        pendingTabSelectionRef.current = null; // Clear the ref after using it
      }
      
      // If we just pasted a tab, don't let useEffect overwrite ANY elements
      // Just select the pasted tab and return early to preserve all tabs' elements
      if (justPastedTabRef.current) {
        const pastedTab = datasheet.tabs.find((tab) => tab.id === justPastedTabRef.current);
        if (pastedTab) {
          // Elements were already set in handleTabPaste
          // Just select the pasted tab and ensure it has elements, but don't overwrite ANYTHING
          const pastedTabElements = datasheetElementsStorage.getElements(pastedTab.id);
          if (pastedTabElements && Object.keys(pastedTabElements).length > 0) {
            setCurrentTabId(pastedTab.id);
            setTabDatasheetElements(pastedTabElements, pastedTab.id);
            const historySequence = datasheetElementsStorage.getHistorySequence(pastedTab.id);
            const lastHistoryRecordId = historySequence?.[historySequence.length - 1];
            if (lastHistoryRecordId) {
              setCurrentDatasheetElementsHistoryRecordId(lastHistoryRecordId);
            }
          }
          justPastedTabRef.current = null; // Clear the ref after using it
          // CRITICAL: Return early to prevent ANY overwriting of elements
          // All tabs' elements are already in storage and should be preserved
          return;
        }
        justPastedTabRef.current = null; // Clear if tab not found
      }
      
      if (!selectedTab) {
        selectedTab = datasheet.tabs.find((tab) => tab.id == currentTabId);
      }
      
      if (!selectedTab) {
        // Only select first tab if this is a new datasheet load
        // Otherwise, let handleTabDelete handle the selection
        if (isNewDatasheet) {
          selectedTab = datasheet.tabs[0];
        } else {
          // Tab was deleted, but handleTabDelete should have set a new tab
          // Don't change selection here to avoid overriding handleTabDelete
          return;
        }
      }

      if (!selectedTab) return;

      // Only initialize elements/history if this is a new datasheet or the tab doesn't have elements yet
      const existingElements = datasheetElementsStorage.getElements(selectedTab.id);
      const selectedTabElements = elementsByTab[String(selectedTab.id)] || {};
      
      // IMPORTANT: Only initialize with elementsByTab if:
      // 1. It's a new datasheet load, OR
      // 2. The tab has no existing elements in storage AND elementsByTab has elements (from API)
      // This prevents overwriting user-created elements when tabs are added/deleted/pasted
      const shouldInitializeFromApi = shouldResetCache || 
        ((!existingElements || Object.keys(existingElements).length === 0) && 
        Object.keys(selectedTabElements).length > 0);
      
      if (shouldInitializeFromApi) {
        setCurrentTabId(selectedTab.id);
        datasheetElementsStorage.setElements(selectedTab.id, selectedTabElements);
        setDatasheetElements(selectedTabElements);

        const initialHistoryId = generateId();
        datasheetElementsStorage.addHistoryRecord({
          tabId: selectedTab.id,
          currentHistoryRecordId: undefined,
          newHistoryRecordId: initialHistoryId,
          elementsForNewHistoryRecord: selectedTabElements
        });
        setCurrentDatasheetElementsHistoryRecordId(initialHistoryId);

        // Initialize other tabs only on new datasheet load
        // NEVER overwrite existing elements in storage for other tabs
        if (shouldResetCache) {
          datasheet.tabs.filter((tab) => tab.id !== selectedTab!.id).forEach((tab) => {
            // Only initialize if tab has no existing elements in storage
            const tabExistingElements = datasheetElementsStorage.getElements(tab.id);
            if (!tabExistingElements || Object.keys(tabExistingElements).length === 0) {
              const tabElements = elementsByTab[String(tab.id)] || {};
              datasheetElementsStorage.setElements(tab.id, tabElements);

              const historyRecordId = generateId();
              datasheetElementsStorage.addHistoryRecord({
                tabId: tab.id,
                currentHistoryRecordId: undefined,
                newHistoryRecordId: historyRecordId,
                elementsForNewHistoryRecord: tabElements
              });
            }
          });
        }
      } else if (selectedTab.id != currentTabId) {
        // Tab exists and has elements, just update the selection - preserve existing elements
        setCurrentTabId(selectedTab.id);
        // Use existing elements from storage, not from API
        setTabDatasheetElements(existingElements || {}, selectedTab.id);
        const historySequence = datasheetElementsStorage.getHistorySequence(selectedTab.id);
        const lastHistoryRecordId = historySequence?.[historySequence.length - 1];
        if (lastHistoryRecordId) {
          setCurrentDatasheetElementsHistoryRecordId(lastHistoryRecordId);
        }
      } else if (existingElements && Object.keys(existingElements).length > 0) {
        // Same tab, but ensure we're showing the stored elements (not overwriting)
        setTabDatasheetElements(existingElements, selectedTab.id);
      }
    }
  }, [datasheet.id, datasheet.tabs, formatReloadKey]);

  // Helper function to transform FieldPrimaryListDto to DatasheetFieldComponentOriginFieldGetDto
  const transformFieldToDatasheetField = React.useCallback((fieldDto: FieldPrimaryListDto): DatasheetFieldComponentOriginFieldGetDto => {
    // Get field type name from FieldType enum
    const getFieldTypeName = (type: FieldType): string => {
      const typeNames: Record<FieldType, string> = {
        [FieldType.system]: 'System',
        [FieldType.text]: 'Text',
        [FieldType.date]: 'Date',
        [FieldType.number]: 'Number',
        [FieldType.checkbox]: 'Yes/No',
        [FieldType.dropdown]: 'Dropdown',
        [FieldType.image]: 'Image',
        [FieldType.lookupTable]: 'Lookup Table',
        [FieldType.table]: 'Table',
        [FieldType.computed]: 'Computed',
        [FieldType.summary]: 'Summary',
        [FieldType.tableSummary]: 'Table Summary',
        [FieldType.hyperlink]: 'Hyperlink',
        [FieldType.integer]: 'Integer',
      };
      return typeNames[type] || 'Unknown';
    };

    return {
      id: fieldDto.id,
      fieldid: fieldDto.id,
      fieldname: fieldDto.name,
      fieldtype: {
        typeid: fieldDto.type,
        typename: getFieldTypeName(fieldDto.type),
        imageURL: fieldDto.imageURL,
        fieldClass: '',
        computed: fieldDto.type === FieldType.computed,
        systemType: fieldDto.system,
      },
      permissions: '',
      tableIndex: 0,
      defaultWidth: 0,
      imageURL: fieldDto.imageURL,
      fieldClass: '',
      calcField: false,
      computed: fieldDto.type === FieldType.computed,
      rawFieldType: fieldDto.type,
      table: fieldDto.type === FieldType.table,
      type: fieldDto.type,
      systemField: fieldDto.system,
      system: fieldDto.system,
      subfields: undefined,
      lookupTableInfo: undefined,
      fieldnameWithPath: fieldDto.fullPath || fieldDto.name,
      required: false,
      updateColsCount: 0,
      showEditorAllowed: true,
      showUploadAllowed: false,
      remoteFieldName: undefined,
      dataSourceNodeId: 0,
      remoteTableName: '',
      remoteTableKeyField: '',
      localMatchForRemoteKeyField: undefined,
      updateFieldname: fieldDto.name,
      autoEditOnClick: false,
      systemDateField: false,
      remoteTableRowIdentifierFieldname: '',
      fieldcontext: fieldDto.context.toString(),
      tableSubField: false,
      remoteField: false,
      confirmEntry: false,
      hyperlinkfield: fieldDto.type === FieldType.hyperlink,
      imageField: fieldDto.type === FieldType.image,
    };
  }, []);


  const handleSelectedTabChange = async (tabId: LocalDatasheetTabDto['id']) => {
    const numericTabId = Number(tabId);
    // Always try to load format when switching tabs (loadTabFormat no-ops if tab already has fields)
    if (loadTabFormat && numericTabId !== currentTabIdRef.current) {
      await loadTabFormat(numericTabId);
    }

    // Save current tab's elements to storage before switching (if we have a current tab)
    // Use ref to get the most current tab ID to ensure we save the correct tab's elements
    const currentTab = currentTabIdRef.current;
    if (currentTab && currentTab != tabId && datasheetElements) {
      datasheetElementsStorage.setElements(currentTab, datasheetElements);
    }

    // Load elements for the new tab; normalize tab orders to repair any legacy
    // data where all fields share the same tab order value (e.g. all = 0 or 1).
    const tabElements = normalizeDatasheetElementsTabOrder(
      datasheetElementsStorage.getElements(tabId) || {}
    );

    setCurrentTabId(tabId);
    setTabDatasheetElements(tabElements, tabId);
    
    const historySequence = datasheetElementsStorage.getHistorySequence(tabId);
    const lastHistoryRecordId = historySequence?.[historySequence.length - 1];

    if (lastHistoryRecordId) {
      setCurrentDatasheetElementsHistoryRecordId(lastHistoryRecordId);
    } else if (Object.keys(tabElements).length > 0) {
      // Only create history if tab has elements, otherwise it will be created when first element is added
      addNewDatasheetElementsHistoryRecord(tabElements, tabId);
    } else {
      setCurrentDatasheetElementsHistoryRecordId(undefined);
    }
  };

  // Helper function to sync datasheetElements state from datasheet.tabs
  const syncElementsFromTabs = React.useCallback((tabs: LocalDatasheetTabDto[], currentTabIdToSync?: LocalDatasheetTabDto['id']) => {
    const elementsByTab = buildDatasheetElementsByTab(tabs as TabLike[]);

    tabs.forEach((tab) => {
      const existingElements = datasheetElementsStorage.getElements(tab.id);
      if (!existingElements || Object.keys(existingElements).length === 0) {
        const tabElements = elementsByTab[String(tab.id)] || {};
        // Normalize tab orders so fields from the server (which may all be 0 or 1)
        // get a unique sequential sequence before the user can interact with them.
        datasheetElementsStorage.setElements(tab.id, normalizeDatasheetElementsTabOrder(tabElements));
      }
    });

    if (currentTabIdToSync) {
      const existingElements = datasheetElementsStorage.getElements(currentTabIdToSync);
      const rawCurrentTabElements = existingElements && Object.keys(existingElements).length > 0
        ? existingElements
        : (elementsByTab[String(currentTabIdToSync)] || {});
      // Normalize to repair any duplicate/out-of-range tab orders that may have been
      // stored from a previous session or loaded from a legacy server payload.
      const currentTabElements = normalizeDatasheetElementsTabOrder(rawCurrentTabElements);
      setTabDatasheetElements(currentTabElements, currentTabIdToSync);

      if (Object.keys(currentTabElements).length > 0) {
        addNewDatasheetElementsHistoryRecord(currentTabElements, currentTabIdToSync);
      }
    }
  }, [addNewDatasheetElementsHistoryRecord, setTabDatasheetElements, normalizeDatasheetElementsTabOrder]);

  const handleTabAdd = React.useCallback((tabName: string) => {
    const newTab: LocalDatasheetTabDto = {
      id: generateId(),
      name: tabName,
      texts: [],
      fields: []
    };

    const updatedTabs = [...datasheet.tabs, newTab];
    onDatasheetChange({
      ...datasheet,
      tabs: updatedTabs
    });
    datasheetElementsStorage.addTab(newTab.id);
    
    // GWT-like: don't switch to new tab. Only ensure new tab has empty storage;
    // do NOT pass newTab.id to syncElementsFromTabs, or we'd overwrite the
    // displayed canvas with empty while currentTabId still points at the current tab.
    syncElementsFromTabs(updatedTabs);
  }, [datasheet, onDatasheetChange, syncElementsFromTabs]);

  const handleTabInsert = React.useCallback((tabName: string) => {
    // Insert tab next to the currently focused tab
    const currentTab = currentTabIdRef.current;
    if (!currentTab) {
      // If no current tab, just add at the end
      handleTabAdd(tabName);
      return;
    }

    const currentTabIndex = datasheet.tabs.findIndex((tab) => tab.id == currentTab);
    const insertIndex = currentTabIndex === -1 ? datasheet.tabs.length : currentTabIndex + 1;

    const newTab: LocalDatasheetTabDto = {
      id: generateId(),
      name: tabName,
      texts: [],
      fields: []
    };

    // Insert the new tab at the specified index
    const updatedTabs = [
      ...datasheet.tabs.slice(0, insertIndex),
      newTab,
      ...datasheet.tabs.slice(insertIndex)
    ];

    onDatasheetChange({
      ...datasheet,
      tabs: updatedTabs
    });
    datasheetElementsStorage.addTab(newTab.id);
    
    // Sync elements from tabs to state (for the new tab, but don't switch to it)
    syncElementsFromTabs(updatedTabs);
    
    // Keep focus on the current tab - do NOT switch to the newly inserted tab
    // The current tab remains focused
  }, [datasheet, onDatasheetChange, syncElementsFromTabs, handleTabAdd]);

  const handleTabDelete = () => {
    // Simple approach: Remove the tab from datasheet.tabs array
    const tabIdToDelete = currentTabIdRef.current;
    
    if (!tabIdToDelete) {
      return;
    }

    // Only prevent if it's the absolute last tab
    if (datasheet.tabs.length <= 1) {
      return;
    }

    // Find the index of the current tab being deleted
    const currentTabIndex = datasheet.tabs.findIndex((tab) => tab.id == tabIdToDelete);
    
    // If tab not found, return early
    if (currentTabIndex === -1) {
      return;
    }
    
    // Simply remove the tab from the array - this maintains the state correctly
    const nextTabs = datasheet.tabs.filter((tab) => tab.id != tabIdToDelete);

    // Determine which tab to select after deletion
    let nextTab: LocalDatasheetTabDto | undefined;
    
    if (nextTabs.length > 0) {
      // Try to select the next tab (same index)
      if (currentTabIndex < nextTabs.length) {
        nextTab = nextTabs[currentTabIndex];
      } else {
        // If no next tab exists, select the previous tab (index - 1)
        nextTab = nextTabs[currentTabIndex - 1];
      }
    }

    // Update datasheet - simply remove the tab from the array
    onDatasheetChange({
      ...datasheet,
      tabs: nextTabs
    });

    // Print tabs data after deleting operation
    console.log('Tabs data after deleting:', JSON.stringify(nextTabs, null, 2));
    console.log('Tabs array after deleting:', nextTabs);

    // Clean up storage for deleted tab
    datasheetElementsStorage.removeTab(tabIdToDelete);
    datasheetElementsStorage.deleteHistory(tabIdToDelete);

    // Switch to the next tab and sync elements from tabs
    if (nextTab) {
      setCurrentTabId(nextTab.id);
      // Sync elements from tabs to state for the next tab
      syncElementsFromTabs(nextTabs, nextTab.id);
    }
  };

  const handleTabCopy = React.useCallback(() => {
    // Simple approach: Copy the entire tab object from datasheet.tabs array
    const tabIdToCopy = currentTabIdRef.current;
    if (!tabIdToCopy) {
      return;
    }

    // CRITICAL: Save current tab's elements to storage first (if we have unsaved changes)
    // This ensures we have the latest elements before copying
    if (datasheetElements && Object.keys(datasheetElements).length > 0) {
      datasheetElementsStorage.setElements(tabIdToCopy, datasheetElements);
    }

    // Find the tab in the datasheet.tabs array
    const tabToCopy = datasheet.tabs.find((tab) => tab.id == tabIdToCopy);
    if (!tabToCopy) {
      return;
    }

    // Deep copy the entire tab object (including fields and texts arrays)
    const copiedTab: LocalDatasheetTabDto = {
      ...tabToCopy,
      id: tabToCopy.id, // Keep original ID for reference, will be regenerated on paste
      fields: tabToCopy.fields.map((field) => ({ ...field })),
      texts: tabToCopy.texts.map((text) => ({ ...text })),
    };

    // CRITICAL: Deep copy the actual elements from storage (with positions, styles, etc.)
    // Get elements from storage (now guaranteed to be up-to-date after saving above)
    const tabElements = datasheetElementsStorage.getElements(tabIdToCopy) || {};
    
    // Deep copy each element to avoid reference issues
    // This ensures modifications during paste don't affect the source tab's elements
    const deepCopiedElements: Record<DatasheetElementId, DatasheetElement> = {};
    Object.entries(tabElements).forEach(([id, element]: [string, DatasheetElement]) => {
      if (element.type === DatasheetElementType.text) {
        const textElement = element as DatasheetElement<DatasheetTextComponentGetDto>;
        // Deep copy all nested objects
        deepCopiedElements[id] = {
          ...textElement,
          original: structuredClone(textElement.original) as DatasheetTextComponentGetDto,
          coordinates: { ...textElement.coordinates },
        };
      } else {
        const fieldElement = element as DatasheetElement<DatasheetFieldComponentGetDto>;
        // Deep copy all nested objects including the field object
        deepCopiedElements[id] = {
          ...fieldElement,
          original: structuredClone(fieldElement.original) as DatasheetFieldComponentGetDto,
          coordinates: { ...fieldElement.coordinates },
        };
      }
    });
    
    copiedTabElementsRef.current = deepCopiedElements;

    // Store the copied tab object
    setCopiedDatasheetElementsFromTab(copiedTab as any); // Store as tab object
    copiedDatasheetElementsFromTabRef.current = copiedTab as any;
    setHasCopiedTab(true);
    hasCopiedTabRef.current = true;
    
    // Save to sessionStorage for cross-datasheet copy/paste
    saveCopiedTabToStorage(copiedTab, deepCopiedElements);
  }, [datasheet.tabs, datasheetElementsStorage, datasheetElements]);

  const handleTabPaste = (tabName: string) => {
    // Try to load from sessionStorage first (for cross-datasheet paste)
    // Fall back to refs if sessionStorage doesn't have it (same datasheet paste)
    let copiedTab: LocalDatasheetTabDto | null = null;
    let copiedElements: Record<DatasheetElementId, DatasheetElement> = {};
    
    const storageData = loadCopiedTabFromStorage();
    if (storageData) {
      copiedTab = storageData.tab;
      copiedElements = storageData.elements;
    } else {
      // Fall back to refs for same-datasheet paste
      copiedTab = copiedDatasheetElementsFromTabRef.current as any as LocalDatasheetTabDto;
      copiedElements = copiedTabElementsRef.current;
    }
    
    if (!copiedTab || (!hasCopiedTabRef.current && !storageData)) {
      return;
    }

    // CRITICAL: Save ALL tabs' elements to storage before pasting
    // This ensures ALL tabs' elements are preserved, especially the source tab
    // First, save current tab's elements from state if we have unsaved changes
    const currentTab = currentTabIdRef.current;
    if (currentTab && datasheetElements && Object.keys(datasheetElements).length > 0) {
      datasheetElementsStorage.setElements(currentTab, datasheetElements);
    }
    
    // Get the source tab ID (the tab that was copied)
    const sourceTabId = copiedTab.id;
    
    // CRITICAL: Explicitly save the source tab's elements to storage
    // This ensures the source tab's elements are preserved even if they're not in current state
    if (sourceTabId) {
      // First try to get from current state if it's the current tab
      let sourceTabElements = currentTab === sourceTabId && datasheetElements 
        ? datasheetElements 
        : datasheetElementsStorage.getElements(sourceTabId);
      
      // If we have elements, save them to ensure they're preserved
      if (sourceTabElements && Object.keys(sourceTabElements).length > 0) {
        datasheetElementsStorage.setElements(sourceTabId, sourceTabElements);
      }
    }
    
    // Save all other tabs' elements to storage as well
    datasheet.tabs.forEach((tab) => {
      // Skip if this is the current tab (already saved above) or source tab (already saved above)
      if (tab.id === currentTab || tab.id === sourceTabId) {
        return;
      }
      const tabElements = datasheetElementsStorage.getElements(tab.id);
      if (tabElements && Object.keys(tabElements).length > 0) {
        // Elements already in storage, keep them
        datasheetElementsStorage.setElements(tab.id, tabElements);
      }
    });

    // Generate new ID first to avoid temporal dead zone error
    const newTabId = generateId();
    const newTabIdNumber = typeof newTabId === 'number' ? newTabId : 0;

    // Create a deep copy of the entire tab object with new ID
    const newTab: LocalDatasheetTabDto = {
      ...copiedTab,
      id: newTabId,
      name: tabName,
      // Deep copy fields array
      fields: copiedTab.fields.map((field) => ({
        ...field,
        tabid: newTabIdNumber,
      })),
      // Deep copy texts array
      texts: copiedTab.texts.map((text) => ({
        ...text,
        tabId: newTabIdNumber,
      })),
    };

    // Set justPastedTabRef BEFORE calling onDatasheetChange
    // This prevents the useEffect from overwriting elements
    justPastedTabRef.current = newTabId;

    // Simply add the new tab to the datasheet.tabs array
    // This maintains the state correctly
    const updatedTabs = [...datasheet.tabs, newTab];
    const updatedDatasheet = {
      ...datasheet,
      tabs: updatedTabs
    };
    onDatasheetChange(updatedDatasheet);

    // Print tabs data after pasting operation
    console.log('Tabs data after pasting:', JSON.stringify(updatedTabs, null, 2));
    console.log('Tabs array:', updatedTabs);

    // CRITICAL: Copy the actual elements (with positions, styles, etc.)
    // These are the elements that were copied when the tab was copied
    // copiedElements is already set above from sessionStorage or refs
    if (copiedElements && Object.keys(copiedElements).length > 0) {
      // Create new elements with new IDs and updated tab IDs
      const newElements: Record<DatasheetElementId, DatasheetElement> = {};
      
      Object.values(copiedElements).forEach((element) => {
        let newElement: DatasheetElement;
        
        if (element.type === DatasheetElementType.text) {
          // For text elements, generate new ID and update tabId
          const textElement = element as DatasheetElement<DatasheetTextComponentGetDto>;
          const newId = generateId();
          // Deep copy the original object to avoid modifying the source element
          newElement = {
            ...textElement,
            id: newId,
            coordinates: { ...textElement.coordinates },
            original: {
              ...textElement.original,
              tabId: newTabIdNumber,
            } as DatasheetTextComponentGetDto
          };
        } else {
          // For field elements, use getUniqueDatasheetElementId to maintain uniqueness
          const fieldElement = element as DatasheetElement<DatasheetFieldComponentGetDto>;
          const uniqueId = getUniqueDatasheetElementId({
            fieldid: fieldElement.original.field.fieldid,
            systemField: fieldElement.original.field.systemField ?? false,
            type: fieldElement.original.field.type,
          });
          // Deep copy the original object and field to avoid modifying the source element
          newElement = {
            ...fieldElement,
            id: uniqueId,
            coordinates: { ...fieldElement.coordinates },
            original: {
              ...fieldElement.original,
              field: { ...fieldElement.original.field },
              tabid: newTabIdNumber,
            } as DatasheetFieldComponentGetDto
          };
        }
        
        newElements[newElement.id] = newElement;
      });
      
      // Store the new elements in storage for the new tab
      datasheetElementsStorage.setElements(newTabId, newElements);
      
      // Set the elements state for the new tab
      setTabDatasheetElements(newElements, newTabId);
      
      // Add history record for the new tab
      if (Object.keys(newElements).length > 0) {
        addNewDatasheetElementsHistoryRecord(newElements, newTabId);
      }
    } else {
      // If no elements were copied, initialize empty storage for the new tab
      datasheetElementsStorage.setElements(newTabId, {});
      datasheetElementsStorage.addTab(newTabId);
      // Don't call syncElementsFromTabs as it would overwrite other tabs' elements
    }

    // Switch to the new tab
    setCurrentTabId(newTab.id);
    
    // Clear copied tab after pasting
    setCopiedDatasheetElementsFromTab({} as any);
    copiedDatasheetElementsFromTabRef.current = {} as any;
    copiedTabElementsRef.current = {};
    setHasCopiedTab(false);
    hasCopiedTabRef.current = false;
    
    // Clear sessionStorage after pasting
    clearCopiedTabFromStorage();
  };

  const isFieldAddedToDatasheet = React.useCallback((field: FieldPickerField) => {
    const id = getUniqueDatasheetElementId({
      fieldid: field.original.id,
      systemField: field.original.system,
      type: field.original.type,
    });
    // Back-compat: datasheets loaded from BE may still use legacy ids like `field-<fieldid>`
    const legacyId = `field-${field.original.id}`;

    return Boolean(datasheetElements[id] || datasheetElements[legacyId]);
  }, [datasheetElements]);

  const updateDatasheetElement = React.useCallback((id: DatasheetElementId, data: Partial<DatasheetElement>) => {
    const datasheetElement = getDatasheetElement(id);

    if (!datasheetElement) {
      return;
    }

    const coordinatesChanged = data.coordinates && (
      data.coordinates.x !== datasheetElement.coordinates.x || 
      data.coordinates.y !== datasheetElement.coordinates.y
    );
    
    if (coordinatesChanged && data.coordinates) {
      const coordinatesDiff = {
        x: data.coordinates.x - datasheetElement.coordinates.x,
        y: data.coordinates.y - datasheetElement.coordinates.y,
      };

      const selectedDatasheets = selectedDatasheetElementsId.reduce((acc, elementId) => {
        const element = getDatasheetElement(elementId);

        if (element) {
          const updatedElement = {
            ...element,
            coordinates: {
              x: element.coordinates.x + coordinatesDiff.x,
              y: element.coordinates.y + coordinatesDiff.y,
            }
          };

          acc[elementId] = updatedElement;
        }

        return acc;
      }, { ...datasheetElements });
      
      setTabDatasheetElements(selectedDatasheets);

      return selectedDatasheets;
    }

    const updatedElement: DatasheetElement = {
      ...datasheetElement,
      ...data,
    };
    

    const nextDatasheetElements = {
      ...datasheetElements,
      [id]: updatedElement,
    };

    setTabDatasheetElements(nextDatasheetElements);

    return nextDatasheetElements;
  }, [
    getDatasheetElement, 
    selectedDatasheetElementsId, 
    datasheetElements, 
    setTabDatasheetElements, 
    currentTabId,
    currentDatasheetElementsHistoryRecordId
  ]);

  const handleDatasheetElementChange = React.useCallback((id: DatasheetElementId, data: Partial<DatasheetElement>) => {
    const newElements = updateDatasheetElement(id, data);

    if (newElements) {
      updateDatasheetElementsHistoryRecord(newElements);
    }
  }, [updateDatasheetElement, updateDatasheetElementsHistoryRecord]);

  const updateDatasheetElementAndAddHistoryRecord = React.useCallback((id: DatasheetElementId, data: Partial<DatasheetElement>) => {
    const newElements = updateDatasheetElement(id, data);

    if (newElements) {
      addNewDatasheetElementsHistoryRecord(newElements);
    }
  }, [updateDatasheetElement, addNewDatasheetElementsHistoryRecord]);

  const handleDatasheetElementResize = updateDatasheetElementAndAddHistoryRecord;
  const handleDatasheetElementMove = updateDatasheetElementAndAddHistoryRecord;

  const handleDatasheetElementsDrop = React.useCallback(({ firstDatasheetElementCoordinates, draggedField }: { firstDatasheetElementCoordinates: { x: number; y: number }; draggedField?: FieldPickerField }) => {
    let firstSelectedDatasheetElement: DatasheetElement | undefined;
    let previousElementId: string | undefined;
    const existingFieldCount = Object.values(datasheetElements)
      .filter((x) => {
        if (x.type !== DatasheetElementType.field) return false;
        const el = x as DatasheetElement<DatasheetFieldComponentGetDto>;
        const ef = el.original.field;
        const eft = (ef as any)?.fieldtype?.typeid ?? (ef as any)?.type ?? (ef as any)?.rawFieldType;
        return isFieldTypeTabNavigable(eft, Boolean(ef?.system));
      })
      .length;

    const existingSystemFieldCount = Object.values(datasheetElements)
      .filter((x) => {
        if (x.type !== DatasheetElementType.field) return false;
        return Boolean((x as DatasheetElement<DatasheetFieldComponentGetDto>).original.field?.system);
      })
      .length;

    const existingNonTabFieldCount = Object.values(datasheetElements)
      .filter((x) => {
        if (x.type !== DatasheetElementType.field) return false;
        const el = x as DatasheetElement<DatasheetFieldComponentGetDto>;
        const f = el.original.field;
        if (Boolean(f?.system)) return false;
        const ft = (f as any)?.fieldtype?.typeid ?? (f as any)?.type ?? (f as any)?.rawFieldType;
        return NON_TAB_FIELD_TYPES.has(ft);
      })
      .length;

    const fieldsToAdd = resolveFieldsToAddOnDrop({
      draggedField,
      selectedFieldsInFieldPicker,
    });

    const nextElements = {
      ...datasheetElements,
      ...fieldsToAdd.reduce<Record<DatasheetElementId, DatasheetElement>>((acc, field) => {
        const id = getUniqueDatasheetElementId({
          fieldid: field.original.id,
          systemField: field.original.system,
          type: field.original.type,
        });

        // Back-compat: datasheets loaded from BE may still use legacy ids like `field-<fieldid>`
        const legacyId = `field-${field.original.id}`;

        if (datasheetElements[id] || datasheetElements[legacyId]) {
          return acc;
        }

        const previousElement = previousElementId ? acc[previousElementId] : undefined;

        const transformedField = transformFieldToDatasheetField(field.original);

        const mappedSettings = datasheetElementSettingsController.mapFieldComponentSettingsToElement(defaultFieldComponentSettings);
        const tabidForNewField = currentTabId != null
          ? (typeof currentTabId === "number" ? currentTabId : parseInt(String(currentTabId), 10))
          : 0;

        const isDroppedSystemField = Boolean(field.original.system);

        const droppedFt = (field.original as any)?.fieldtype?.typeid ?? field.original.type ?? (field.original as any)?.rawFieldType;
        const isDroppedNonTabEligible = !isDroppedSystemField && NON_TAB_FIELD_TYPES.has(droppedFt);

        const fieldsInAccCount = isDroppedSystemField || isDroppedNonTabEligible ? 0 : Object.values(acc)
          .filter((x) => {
            if (x.type !== DatasheetElementType.field) return false;
            const el = x as DatasheetElement<DatasheetFieldComponentGetDto>;
            const ef = el.original.field;
            const eft = (ef as any)?.fieldtype?.typeid ?? (ef as any)?.type ?? (ef as any)?.rawFieldType;
            return isFieldTypeTabNavigable(eft, Boolean(ef?.system));
          })
          .length;
        const systemFieldsInAccCount = isDroppedSystemField ? Object.values(acc)
          .filter((x) => {
            if (x.type !== DatasheetElementType.field) return false;
            return Boolean((x as DatasheetElement<DatasheetFieldComponentGetDto>).original.field?.system);
          })
          .length : 0;
        const nonTabFieldsInAccCount = isDroppedNonTabEligible ? Object.values(acc)
          .filter((x) => {
            if (x.type !== DatasheetElementType.field) return false;
            const el = x as DatasheetElement<DatasheetFieldComponentGetDto>;
            const f = el.original.field;
            if (Boolean(f?.system)) return false;
            const ft = (f as any)?.fieldtype?.typeid ?? (f as any)?.type ?? (f as any)?.rawFieldType;
            return NON_TAB_FIELD_TYPES.has(ft);
          })
          .length : 0;
        const tabOrderForNewField = isDroppedSystemField
          ? existingSystemFieldCount + systemFieldsInAccCount + 1
          : isDroppedNonTabEligible
            ? existingNonTabFieldCount + nonTabFieldsInAccCount + 1
            : existingFieldCount + fieldsInAccCount + 1;

        const originalFieldComponent: DatasheetFieldComponentGetDto = {
          field: transformedField,
          ...mappedSettings,
          headcolor: 0,
          headtext: field.original.name,
          tabOrder: tabOrderForNewField,
          tabid: tabidForNewField,
        } as DatasheetFieldComponentGetDto;

        // TODO remove as

        const datasheetElement: DatasheetElement = {
          id,
          type: DatasheetElementType.field,
          original: originalFieldComponent,
          width: DATASHEET_DEFAULT_FIELDS_DIMENSIONS[field.original.type].width,
          height: DATASHEET_DEFAULT_FIELDS_DIMENSIONS[field.original.type].height,
          coordinates: {
            x: firstDatasheetElementCoordinates.x,
            y: getDroppedFieldYCoordinate({
              firstDroppedFieldY: firstDatasheetElementCoordinates.y,
              previousElement,
              verticalGrid: datasheet.gridSettings.verticalGrid,
              snapToGrid: datasheet.gridSettings.snapToGrid,
            }),
          }
        };

        acc[id] = datasheetElement;
        previousElementId = id;

        if (!firstSelectedDatasheetElement) {
          firstSelectedDatasheetElement = datasheetElement;
        }

        return acc;
      }, {})
    };
    
    setSelectedDatasheetElementsId(firstSelectedDatasheetElement 
      ? [firstSelectedDatasheetElement.id]
      : []);
    setTabDatasheetElementsAndAddHistoryRecord(nextElements);
    onSelectedFieldsInFieldPickerChange?.([]);

  }, [
    datasheetElements, 
    selectedFieldsInFieldPicker, 
    onSelectedFieldsInFieldPickerChange,
    currentTabId, 
    datasheet.gridSettings.verticalGrid,
    datasheet.gridSettings.snapToGrid,
    defaultFieldComponentSettings,
    datasheetElementSettingsController.mapFieldComponentSettingsToElement,
    setTabDatasheetElementsAndAddHistoryRecord,
    transformFieldToDatasheetField,
  ]);

  const handleDatasheetTextElementsDrop = React.useCallback(() => {
    const originalTextComponent: DatasheetTextComponentGetDto =
      datasheetElementSettingsController.mapTextComponentSettingsToElement(
        defaultTextComponentSettings
      ) as DatasheetTextComponentGetDto;

    const id = generateId();
    const datasheetElement: DatasheetElement<DatasheetTextComponentGetDto>  = {
      id,
      type: DatasheetElementType.text,
      original: originalTextComponent,
      width: textElementDimensions.width,
      height: textElementDimensions.height,
      coordinates: {
        x: datasheet.gridSettings.horizontalGrid,
        y: datasheet.gridSettings.verticalGrid
      }
    };

    const nextElements = {
      ...datasheetElements,
      [id]: datasheetElement
    };
    
    setSelectedDatasheetElementsId([id]);
    setTabDatasheetElementsAndAddHistoryRecord(nextElements);
  }, [
    datasheetElements,
    defaultTextComponentSettings,
    datasheetElementSettingsController.mapTextComponentSettingsToElement,
    datasheet.gridSettings.horizontalGrid,
    datasheet.gridSettings.verticalGrid,
    setTabDatasheetElementsAndAddHistoryRecord,
  ]);

  const handleDatasheetElementsRemove = React.useCallback(() => {
    const nextDatasheetElements = { ...datasheetElements };

    selectedDatasheetElementsId.forEach((elementId) => {
      delete nextDatasheetElements[elementId];
    });

    const normalizedElements = normalizeDatasheetElementsTabOrder(nextDatasheetElements);
    setTabDatasheetElementsAndAddHistoryRecord(normalizedElements);
    datasheetElementsSelectionController.handleSelectionReset();
  }, [
    datasheetElements, 
    selectedDatasheetElementsId, 
    datasheetElementsSelectionController.handleSelectionReset, 
    currentTabId, 
    setTabDatasheetElementsAndAddHistoryRecord,
  ]);

  useDeleteKeyPress(handleDatasheetElementsRemove);

  const datasheetElementsProcessed = React.useMemo(() => {
    return Object.values(datasheetElements);
  }, [datasheetElements]);

  const handleGridSettingsChange = React.useCallback((settings: DatasheetGridSettingsDto) => {
    onDatasheetChange({
      ...datasheet,
      gridSettings: settings
    });

    if (!datasheet.gridSettings.showEditTabOrder && settings.showEditTabOrder) {
      resetSelectedAndCopiedDatasheetElements();
    }
  }, [datasheet, onDatasheetChange, resetSelectedAndCopiedDatasheetElements]);

  const handleSelectAllDatasheetElements = React.useCallback(() => {
    setSelectedDatasheetElementsId(Object.keys(datasheetElements));
  }, [datasheetElements]);

  const handleDatasheetElementsUndo = React.useCallback(() => {
    const currentHistoryRecordId = currentDatasheetElementsHistoryRecordId;
    const historySequence = datasheetElementsStorage.getHistorySequence(currentTabId!);

    if (!currentHistoryRecordId || !historySequence) {
      return;
    }

    const currentHistoryIndex = historySequence.indexOf(currentHistoryRecordId);
    const previousHistoryRecordId = historySequence[currentHistoryIndex - 1];

    if (!previousHistoryRecordId) {
      return;
    }

    const previousHistoryRecord = datasheetElementsStorage.getHistoryRecord(currentTabId!, previousHistoryRecordId);

    if (!previousHistoryRecord) {
      return;
    }

    setTabDatasheetElements({ ...previousHistoryRecord });
    setCurrentDatasheetElementsHistoryRecordId(previousHistoryRecordId);
  }, [currentDatasheetElementsHistoryRecordId, currentTabId, setTabDatasheetElements]);

  const handleDatasheetElementsRedo = React.useCallback(() => {
    const currentHistoryRecordId = currentDatasheetElementsHistoryRecordId;
    const historySequence = datasheetElementsStorage.getHistorySequence(currentTabId!);

    if (!currentHistoryRecordId || !historySequence) {
      return;
    }

    const currentHistoryIndex = historySequence.indexOf(currentHistoryRecordId);
    const nextHistoryRecordId = historySequence[currentHistoryIndex + 1];

    if (!nextHistoryRecordId) {
      return;
    }

    const nextHistoryRecord = datasheetElementsStorage.getHistoryRecord(currentTabId!, nextHistoryRecordId);

    if (!nextHistoryRecord) {
      return;
    }

    setTabDatasheetElements({ ...nextHistoryRecord });
    setCurrentDatasheetElementsHistoryRecordId(nextHistoryRecordId);
  }, [currentDatasheetElementsHistoryRecordId, currentTabId, setTabDatasheetElements]);

  return {
    ...datasheetSavingController,
    ...datasheetElementsSelectionController,
    ...datasheetFormatManagementController,
    ...tabsController,
    ...datasheetElementSettingsController,
    ...datasheetElementsAlignmentController,
    ...datasheetElementsCopyPasteController,
    ...gridSettingsController,
    ...datasheetUndoRedoController,
    handleTabAdd,
    handleTabInsert,
    handleTabDelete,
    handleTabCopy,
    handleTabPaste,
    handleDatasheetElementsUndo,
    handleDatasheetElementsRedo,
    handleDatasheetElementResize,
    handleDatasheetElementMove,
    handleSelectAllDatasheetElements,
    defaultFieldComponentSettings,
    setDefaultFieldComponentSettings,
    handleGridSettingsChange,
    handleDatasheetElementsDrop,
    handleDatasheetTextElementsDrop,
    currentTabId,
    handleSelectedTabChange,
    selectedFieldsInFieldPicker,
    isFieldAddedToDatasheet,
    datasheetElementsProcessed,
    handleDatasheetElementChange,
    defaultTextComponentSettings,
    setDefaultTextComponentSettings,
    currentTextComponentValue: datasheetElementSettingsController.currentTextComponentValue,
    handleTextComponentSettingsChange: datasheetElementSettingsController.handleTextComponentSettingsChange,
  };
};
